import Modal from "react-modal";
import {X} from "react-feather";
import React, {useState, useEffect} from "react";
import DropdownSelect from "../Formik/DropdownSelect";
import {useFormik} from "formik";
import {adminGetCompanyAccounts, getAdminFilterBrokers} from "../../services/api/admin";

const MergeSSOAccountsModal = ({
                                   isOpen,
                                   onClose,
                                   tenantData,
                                   premergeFromAccount,
                                   premergeIntoAccount,
                                   onDryRun,
                                   onMerge
                               }) => {
    const [mergeFromAccount, setMergeFromAccount] = useState(null);
    const [mergeIntoAccount, setMergeIntoAccount] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showSummary, setShowSummary] = useState(false);
    const [summaryData, setSummaryData] = useState(null);
    const [isDryRun, setIsDryRun] = useState(false);
    const [accountsListFrom, setAccountsListFrom] = useState([]);
    const [accountsListInto, setAccountsListInto] = useState([]);
    const [isLoadingAccountsFrom, setIsLoadingAccountsFrom] = useState(false);
    const [isLoadingAccountsInto, setIsLoadingAccountsInto] = useState(false);
    const [accountsError, setAccountsError] = useState(null);
    const [searchTermFrom, setSearchTermFrom] = useState("");
    const [searchTermInto, setSearchTermInto] = useState("");
    const [isLoadingPremergeAccounts, setIsLoadingPremergeAccounts] = useState(false);

    // Tenant selection state (when tenantData is not provided)
    const [searchTenantTerm, setSearchTenantTerm] = useState("");
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [tenantOptions, setTenantOptions] = useState([]);
    const [isLoadingTenants, setIsLoadingTenants] = useState(false);

    // Determine if we need tenant selection (when tenantData is null)
    const needsTenantSelection = !tenantData;

    // Use selected tenant or passed tenantData
    const activeTenantData = selectedTenant || tenantData;

    const formatRole = (role) => {
        if (!role) return '';

        return role.toLowerCase().split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    // Fetch brokers based on search term
    useEffect(() => {
        const fetchTenants = async () => {
            if (!needsTenantSelection) return;

            // Only search if 3 or more characters, or if empty (show all)
            if (searchTenantTerm.length < 3) {
                return;
            }

            setIsLoadingTenants(true);
            setAccountsError(null);

            try {
                const tenants = await getAdminFilterBrokers(searchTenantTerm || '');

                if (tenants) {
                    // Transform to dropdown options
                    // API returns: { id: 123, name: "Alera" }
                    const options = tenants.map(tenant => ({
                        id: tenant.id,
                        name: tenant.name,
                        value: tenant.id,
                        tenant_id: tenant.id,
                        tenant_name: tenant.name,
                        tenant_type: 'broker'
                    }));
                    setTenantOptions(options);
                } else {
                    setAccountsError('Failed to load brokers');
                }
            } catch (error) {
                console.error('Error fetching brokers:', error);
                setAccountsError('Error loading brokers. Please try again.');
            } finally {
                setIsLoadingTenants(false);
            }
        };

        if (isOpen && needsTenantSelection) {
            const timeoutId = setTimeout(() => {
                fetchTenants();
            }, 300);

            return () => clearTimeout(timeoutId);
        } else {
            setTenantOptions([]);
            setSearchTenantTerm("");
        }
    }, [isOpen, needsTenantSelection, searchTenantTerm]);

    // Fetch company accounts for "Merge From" dropdown
    useEffect(() => {
        const fetchCompanyAccountsFrom = async () => {
            if (!isOpen || !activeTenantData?.tenant_id || !activeTenantData?.tenant_type) return;

            // Only search if 3 or more characters, or if empty (show all)
            if (searchTermFrom && searchTermFrom.length > 0 && searchTermFrom.length < 3) {
                // Don't fetch when 1-2 characters typed
                return;
            }

            setIsLoadingAccountsFrom(true);
            setAccountsError(null);

            try {
                const accounts = await adminGetCompanyAccounts(
                    activeTenantData.tenant_id,
                    activeTenantData.tenant_type,
                    searchTermFrom || null
                );

                if (accounts) {
                    setAccountsListFrom(accounts);
                } else {
                    setAccountsError('Failed to load company accounts');
                }
            } catch (error) {
                console.error('Error fetching company accounts:', error);
                setAccountsError('Error loading company accounts. Please try again.');
            } finally {
                setIsLoadingAccountsFrom(false);
            }
        };

        if (isOpen) {
            // Debounce the API call
            const timeoutId = setTimeout(() => {
                fetchCompanyAccountsFrom();
            }, 300);

            return () => clearTimeout(timeoutId);
        } else {
            // Reset when modal closes
            setAccountsListFrom([]);
            setSearchTermFrom("");
        }
    }, [isOpen, activeTenantData?.tenant_id, activeTenantData?.tenant_type, searchTermFrom]);

    // Fetch company accounts for "Merge Into" dropdown
    useEffect(() => {
        const fetchCompanyAccountsInto = async () => {
            if (!isOpen || !activeTenantData?.tenant_id || !activeTenantData?.tenant_type) return;

            // Only search if 3 or more characters, or if empty (show all)
            if (searchTermInto && searchTermInto.length > 0 && searchTermInto.length < 3) {
                // Don't fetch when 1-2 characters typed
                return;
            }

            setIsLoadingAccountsInto(true);
            setAccountsError(null);

            try {
                const accounts = await adminGetCompanyAccounts(
                    activeTenantData.tenant_id,
                    activeTenantData.tenant_type,
                    searchTermInto || null
                );

                if (accounts) {
                    setAccountsListInto(accounts);
                } else {
                    setAccountsError('Failed to load company accounts');
                }
            } catch (error) {
                console.error('Error fetching company accounts:', error);
                setAccountsError('Error loading company accounts. Please try again.');
            } finally {
                setIsLoadingAccountsInto(false);
            }
        };

        if (isOpen) {
            // Debounce the API call
            const timeoutId = setTimeout(() => {
                fetchCompanyAccountsInto();
            }, 300);

            return () => clearTimeout(timeoutId);
        } else {
            // Reset when modal closes
            setAccountsListInto([]);
            setSearchTermInto("");
        }
    }, [isOpen, activeTenantData?.tenant_id, activeTenantData?.tenant_type, searchTermInto]);

    // Initial load of all accounts
    useEffect(() => {
        const fetchInitialAccounts = async () => {
            if (!isOpen || !activeTenantData?.tenant_id || !activeTenantData?.tenant_type) return;

            try {
                const accounts = await adminGetCompanyAccounts(
                    activeTenantData.tenant_id,
                    activeTenantData.tenant_type,
                    null
                );

                if (accounts) {
                    setAccountsListFrom(accounts);
                    setAccountsListInto(accounts);
                }
            } catch (error) {
                console.error('Error fetching initial company accounts:', error);
            }
        };

        if (isOpen) {
            fetchInitialAccounts();
        } else {
            setAccountsError(null);
        }
    }, [isOpen, activeTenantData?.tenant_id, activeTenantData?.tenant_type]);

    // Fetch specific account details for premerge accounts
    useEffect(() => {
        const fetchPremergeAccountDetails = async () => {
            if (!isOpen || !activeTenantData?.tenant_id || !activeTenantData?.tenant_type) return;
            if (!premergeFromAccount && !premergeIntoAccount) return;

            setIsLoadingPremergeAccounts(true);

            try {
                // Fetch full details for "from" account by email
                let enrichedFromAccount = null;
                if (premergeFromAccount?.email) {
                    const fromAccounts = await adminGetCompanyAccounts(
                        activeTenantData.tenant_id,
                        activeTenantData.tenant_type,
                        premergeFromAccount.email
                    );
                    enrichedFromAccount = fromAccounts?.find(a => a.account_id === premergeFromAccount.account_id) || premergeFromAccount;
                }

                // Fetch full details for "into" account by email
                let enrichedIntoAccount = null;
                if (premergeIntoAccount?.email) {
                    const intoAccounts = await adminGetCompanyAccounts(
                        activeTenantData.tenant_id,
                        activeTenantData.tenant_type,
                        premergeIntoAccount.email
                    );
                    enrichedIntoAccount = intoAccounts?.find(a => a.account_id === premergeIntoAccount.account_id) || premergeIntoAccount;
                }

                // Transform to option format with enriched data
                const transformToOption = (account) => {
                    if (!account) return null;
                    return {
                        id: account.account_id,
                        account_id: account.account_id,
                        email: account.email,
                        name: `${account.email} (Role: ${formatRole(account.role)})`,
                        value: account.account_id,
                        rawData: account
                    };
                };

                // Set enriched accounts
                if (enrichedFromAccount) {
                    const fromOption = transformToOption(enrichedFromAccount);
                    setMergeFromAccount(fromOption);
                    formik.setFieldValue('mergeFrom', fromOption);
                }

                if (enrichedIntoAccount) {
                    const intoOption = transformToOption(enrichedIntoAccount);
                    setMergeIntoAccount(intoOption);
                    formik.setFieldValue('mergeInto', intoOption);
                }

            } catch (error) {
                console.error('Error fetching premerge account details:', error);
                // Fall back to original data if fetch fails
                const transformToOption = (account) => {
                    if (!account) return null;
                    return {
                        id: account.account_id,
                        account_id: account.account_id,
                        email: account.email,
                        name: `${account.email} (Role: ${formatRole(account.role)})`,
                        value: account.account_id,
                        rawData: account
                    };
                };

                if (premergeFromAccount) {
                    const fromOption = transformToOption(premergeFromAccount);
                    setMergeFromAccount(fromOption);
                    formik.setFieldValue('mergeFrom', fromOption);
                }

                if (premergeIntoAccount) {
                    const intoOption = transformToOption(premergeIntoAccount);
                    setMergeIntoAccount(intoOption);
                    formik.setFieldValue('mergeInto', intoOption);
                }
            } finally {
                setIsLoadingPremergeAccounts(false);
            }
        };

        fetchPremergeAccountDetails();
    }, [isOpen, activeTenantData?.tenant_id, activeTenantData?.tenant_type, premergeFromAccount, premergeIntoAccount]);

    // Transform accounts list into options format for dropdown
    const transformAccountsToOptions = (accounts) => {
        return accounts.map(account => {
            return {
                id: account.account_id,
                account_id: account.account_id,
                email: account.email,
                name: `${account.email} (Role: ${formatRole(account.role)})`,
                value: account.account_id,
                rawData: account
            };
        });
    };

    const accountOptionsFrom = transformAccountsToOptions(accountsListFrom);
    const accountOptionsInto = transformAccountsToOptions(accountsListInto);

    // Formik setup for form state management
    const formik = useFormik({
        initialValues: {
            mergeFrom: null,
            mergeInto: null
        },
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: () => {
        }
    });

    const handleMergeFromChange = (selectedOption) => {
        setMergeFromAccount(selectedOption);
        formik.setFieldValue('mergeFrom', selectedOption);
        // Clear error when selecting
        if (selectedOption) {
            formik.setFieldError('mergeFrom', undefined);
        }
    };

    const handleMergeIntoChange = (selectedOption) => {
        setMergeIntoAccount(selectedOption);
        formik.setFieldValue('mergeInto', selectedOption);
        // Clear error when selecting
        if (selectedOption) {
            formik.setFieldError('mergeInto', undefined);
        }
    };

    const validateSelections = () => {
        let isValid = true;

        if (!mergeFromAccount) {
            formik.setFieldError('mergeFrom', 'Please select an account to merge from');
            formik.setFieldTouched('mergeFrom', true);
            isValid = false;
        }

        if (!mergeIntoAccount) {
            formik.setFieldError('mergeInto', 'Please select an account to merge into');
            formik.setFieldTouched('mergeInto', true);
            isValid = false;
        }

        if (!isValid) {
            return false;
        }

        if (mergeFromAccount.id === mergeIntoAccount.id) {
            alert("Please select different accounts for 'Merge from' and 'Merge into'");
            return false;
        }

        return true;
    };

    const handleDryRun = async () => {
        if (!validateSelections()) return;
        setIsLoading(true);
        try {
            const result = await onDryRun(mergeFromAccount, mergeIntoAccount);
            if (result && result.summary) {
                setSummaryData(result.summary);
                setIsDryRun(true);
                setShowSummary(true);
            }
        } catch (error) {
            console.error('Dry run failed:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleMerge = async () => {
        if (!validateSelections()) return;
        setIsLoading(true);
        try {
            const result = await onMerge(mergeFromAccount, mergeIntoAccount);
            if (result && result.summary) {
                setSummaryData(result.summary);
                setIsDryRun(false);
                setShowSummary(true);
            }
        } catch (error) {
            console.error('Merge failed:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setMergeFromAccount(null);
        setMergeIntoAccount(null);
        setShowSummary(false);
        setSummaryData(null);
        setIsDryRun(false);
        // Reset tenant selection state
        setSearchTenantTerm("");
        setSelectedTenant(null);
        setTenantOptions([]);
        formik.resetForm();
        onClose();
    };

    const handleBackToSelection = () => {
        setShowSummary(false);
        setSummaryData(null);
        setIsDryRun(false);
    };

    const renderSummary = () => {
        if (!summaryData) return null;

        const totalLegacy = summaryData.affected_records_legacy || 0;
        const totalNextgen = summaryData.affected_records_nextgen || 0;
        const totalRecords = totalLegacy + totalNextgen;

        return (
            <div className="sl-modal-body sl-px-6 sl-pb-0 sl-pt-24">
                <div className="sl-mb-6">
                    <h2 className="sl-text-lg sl-font-semibold sl-mb-4">
                        {isDryRun ? '🔍 Dry Run Results - Preview' : '✅ Merge Completed Successfully'}
                    </h2>

                    {isDryRun && (
                        <div className="sl-bg-blue-50 sl-border sl-border-blue-200 sl-rounded-lg sl-p-4 sl-mb-4">
                            <p className="sl-text-sm sl-text-blue-800">
                                This is a preview. No changes have been made to the database.
                            </p>
                        </div>
                    )}

                    <div className="sl-bg-gray-50 sl-border sl-border-gray-200 sl-rounded-lg sl-p-4 sl-mb-4">
                        <h3 className="sl-font-semibold sl-mb-2">Summary</h3>
                        <p className="sl-text-sm sl-mb-1">
                            <span className="sl-font-medium">Total Affected Records:</span> {totalRecords}
                        </p>
                        <p className="sl-text-sm sl-mb-1">
                            <span className="sl-font-medium">Legacy System:</span> {totalLegacy} records
                        </p>
                        <p className="sl-text-sm">
                            <span className="sl-font-medium">NextGen System:</span> {totalNextgen} records
                        </p>
                    </div>

                    {summaryData.legacy && Object.keys(summaryData.legacy).some(key => summaryData.legacy[key] > 0) && (
                        <div className="sl-mb-4">
                            <h3 className="sl-font-semibold sl-mb-2">Legacy System - Affected Tables</h3>
                            <div className="sl-max-h-64 sl-overflow-y-auto sl-border sl-border-gray-200 sl-rounded-lg">
                                <table className="sl-w-full sl-text-sm">
                                    <thead className="sl-bg-gray-100 sl-sticky sl-top-0">
                                    <tr>
                                        <th className="sl-text-left sl-p-2 sl-border-b">Table Name</th>
                                        <th className="sl-text-right sl-p-2 sl-border-b">Records</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {Object.entries(summaryData.legacy)
                                        .filter(([_, count]) => count > 0)
                                        .map(([table, count]) => (
                                            <tr key={table} className="sl-border-b">
                                                <td className="sl-p-2">{table}</td>
                                                <td className="sl-text-right sl-p-2 sl-font-medium">{count}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {summaryData.nextgen && Object.keys(summaryData.nextgen).some(key => summaryData.nextgen[key] > 0) && (
                        <div className="sl-mb-4">
                            <h3 className="sl-font-semibold sl-mb-2">NextGen System - Affected Tables</h3>
                            <div className="sl-max-h-64 sl-overflow-y-auto sl-border sl-border-gray-200 sl-rounded-lg">
                                <table className="sl-w-full sl-text-sm">
                                    <thead className="sl-bg-gray-100 sl-sticky sl-top-0">
                                    <tr>
                                        <th className="sl-text-left sl-p-2 sl-border-b">Table Name</th>
                                        <th className="sl-text-right sl-p-2 sl-border-b">Records</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {Object.entries(summaryData.nextgen)
                                        .filter(([_, count]) => count > 0)
                                        .map(([table, count]) => (
                                            <tr key={table} className="sl-border-b">
                                                <td className="sl-p-2">{table}</td>
                                                <td className="sl-text-right sl-p-2 sl-font-medium">{count}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {totalRecords === 0 && (
                        <div className="sl-bg-yellow-50 sl-border sl-border-yellow-200 sl-rounded-lg sl-p-4">
                            <p className="sl-text-sm sl-text-yellow-800">
                                No records found to merge. The selected accounts may not have any associated data.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <Modal
            isOpen={isOpen}
            className="sl-modal-content sl-max-modal"
            overlayClassName="sl-modal sl-block sl-rounded-md sl-bg-gray-900-opacity-50">

            <div className="sl-modal-body">
                <div
                    className="sl-modal-header sl-rounded-md sl-flex sl-justify-between sl-bg-gray-50 sl-text-gray-900 sl-px-6 sl-py-16 sl-border-bottom">
                    <div className="title sl-font-semibold sl-text-lg sl-mr-auto sl-ml-0 sl-font">
                        Managing Potential Duplicate Accounts
                    </div>
                    <div className="sl-cursor-pointer sl-btn-link-gray sl-mr-0">
                        <X onClick={handleClose}/>
                    </div>
                </div>
            </div>

            <div className="sl-modal-body">
                {showSummary ? (
                    renderSummary()
                ) : (
                    <div
                        className="sl-modal-body sl-max-h-185i sl-h-auto sl-px-6 sl-pb-0 sl-pt-24 sl-overflow-y-auto sl-customized-scrollbar sl-font">

                        {/* Tenant Selection (when tenantData is not provided) */}
                        {needsTenantSelection ? (
                            <>
                                {/* Broker Search Dropdown */}
                                <div className="sl-mb-6">
                                    <DropdownSelect
                                        label="Select Broker"
                                        formik={formik}
                                        name="selectedTenant"
                                        selectedItem={selectedTenant}
                                        fieldDefaultText="Search for broker..."
                                        options={tenantOptions}
                                        onChange={(tenant) => {
                                            setSelectedTenant(tenant);
                                            // Clear account selections when tenant changes
                                            setMergeFromAccount(null);
                                            setMergeIntoAccount(null);
                                            formik.setFieldValue('mergeFrom', null);
                                            formik.setFieldValue('mergeInto', null);
                                        }}
                                        onSearchChange={setSearchTenantTerm}
                                        isLoading={isLoadingTenants}
                                        minSearchChars={3}
                                        elementClasses="sl-width-100"
                                    />
                                </div>

                                {/* Info message when broker is selected */}
                                {selectedTenant && (
                                    <div
                                        className="sl-bg-green-50 sl-border sl-border-green-200 sl-rounded-lg sl-p-4 sl-mb-4">
                                        <p className="sl-text-sm sl-text-green-800">
                                            <span
                                                className="sl-font-semibold">Selected Broker:</span> {selectedTenant.tenant_name}
                                        </p>
                                    </div>
                                )}
                            </>
                        ) : (
                            /* Tenant Information (when tenantData is provided) */
                            <div className="sl-mb-6">
                                <p className="sl-text-sm sl-text-gray-600">
                                    <span className="sl-font-semibold">Tenant:</span> {tenantData?.tenant_name}
                                </p>
                                <p className="sl-text-sm sl-text-gray-600">
                                    <span className="sl-font-semibold">Type:</span> {tenantData?.tenant_type}
                                </p>
                            </div>
                        )}

                        {/* Account Selection - Only show when tenant is available */}
                        {activeTenantData && (
                            <>
                                {isLoadingPremergeAccounts ? (
                                    <div
                                        className="sl-bg-blue-50 sl-border sl-border-blue-200 sl-rounded-lg sl-p-4 sl-mb-4">
                                        <p className="sl-text-sm sl-text-blue-800">Loading account details...</p>
                                    </div>
                                ) : accountsError ? (
                                    <div
                                        className="sl-bg-red-50 sl-border sl-border-red-200 sl-rounded-lg sl-p-4 sl-mb-4">
                                        <p className="sl-text-sm sl-text-red-800">{accountsError}</p>
                                    </div>
                                ) : (
                                    <>
                                        {/* Merge From Dropdown */}
                                        <div className="sl-mb-6">
                                            <DropdownSelect
                                                label="Merge from"
                                                formik={formik}
                                                name="mergeFrom"
                                                selectedItem={mergeFromAccount}
                                                fieldDefaultText="Select account to merge from"
                                                options={accountOptionsFrom.filter(option => !mergeIntoAccount || option.id !== mergeIntoAccount.id)}
                                                onChange={handleMergeFromChange}
                                                onSearchChange={setSearchTermFrom}
                                                isLoading={isLoadingAccountsFrom}
                                                minSearchChars={3}
                                                elementClasses="sl-width-100"
                                            />
                                        </div>

                                        {/* Merge Into Dropdown */}
                                        <div className="sl-mb-6">
                                            <DropdownSelect
                                                label="Merge into"
                                                formik={formik}
                                                name="mergeInto"
                                                selectedItem={mergeIntoAccount}
                                                fieldDefaultText="Select account to merge into"
                                                options={accountOptionsInto.filter(item => !mergeFromAccount || item.id !== mergeFromAccount.id)}
                                                onChange={handleMergeIntoChange}
                                                onSearchChange={setSearchTermInto}
                                                isLoading={isLoadingAccountsInto}
                                                minSearchChars={3}
                                                elementClasses="sl-width-100"
                                            />
                                        </div>

                                        {/* Info message */}
                                        <div
                                            className="sl-bg-blue-50 sl-border sl-border-blue-200 sl-rounded-lg sl-p-4 sl-mt-4">
                                            <p className="sl-text-sm sl-text-blue-800">
                                                <span className="sl-font-semibold">Note:</span> The "Merge from" account
                                                will be
                                                merged
                                                into the "Merge into" account.
                                            </p>
                                        </div>
                                    </>
                                )}
                            </>
                        )}

                        {/* Instruction message when no broker selected */}
                        {needsTenantSelection && !selectedTenant && (
                            <div
                                className="sl-bg-yellow-50 sl-border sl-border-yellow-200 sl-rounded-lg sl-p-4 sl-mt-4">
                                <p className="sl-text-sm sl-text-yellow-800">
                                    <span className="sl-font-semibold">Please select a broker</span> to continue with
                                    account merge.
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="sl-modal-footer sl-px-6 sl-py-32 sl-display-flex sl-flex-row sl-justify-between">
                {showSummary ? (
                    <>
                        <div
                            className="sl-flex sl-justify-end sl-gap-2-point-5 sl-bg-white sl-rounded-bottom-left-xl sl-rounded-bottom-right-xl sl-mt-0i sl-font sl-text-md sl-font-normal">
                            {isDryRun && (
                                <button
                                    onClick={handleBackToSelection}
                                    className="sl-btn sl-btn-tertiary sl-btn-lg sl-rounded-lg sl-text-md sl-pl-20 sl-pr-20 sl-font">
                                    Back to Selection
                                </button>
                            )}
                        </div>
                        <div
                            className="sl-flex sl-justify-end sl-gap-2-point-5 sl-bg-white sl-rounded-bottom-left-xl sl-rounded-bottom-right-xl sl-mt-0i sl-font sl-text-md sl-font-normal">
                            <button
                                onClick={handleClose}
                                className="sl-btn sl-btn-primary sl-btn-lg sl-rounded-lg sl-text-md sl-pl-20 sl-pr-20 sl-font">
                                {isDryRun ? 'Close' : 'Done'}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div
                            className="sl-flex sl-justify-end sl-gap-2-point-5 sl-bg-white sl-rounded-bottom-left-xl sl-rounded-bottom-right-xl sl-mt-0i sl-font sl-text-md sl-font-normal">
                            <button
                                onClick={handleClose}
                                disabled={isLoading}
                                className="sl-btn sl-btn-tertiary sl-btn-lg sl-rounded-lg sl-text-md sl-pl-20 sl-pr-20 sl-font">
                                Cancel
                            </button>
                        </div>

                        <div
                            className="sl-flex sl-justify-end sl-gap-2-point-5 sl-bg-white sl-rounded-bottom-left-xl sl-rounded-bottom-right-xl sl-mt-0i sl-font sl-text-md sl-font-normal">
                            <button
                                onClick={handleDryRun}
                                disabled={isLoading || accountsError || !activeTenantData || isLoadingPremergeAccounts}
                                className="sl-btn sl-btn-secondary sl-btn-lg sl-rounded-lg sl-text-md sl-pl-20 sl-pr-20 sl-font">
                                {isLoading ? 'Processing...' : 'Try Dry Run'}
                            </button>
                            <button
                                onClick={handleMerge}
                                disabled={isLoading || accountsError || !activeTenantData || isLoadingPremergeAccounts}
                                className="sl-btn sl-btn-primary sl-btn-lg sl-rounded-lg sl-text-md sl-pl-20 sl-pr-20 sl-font">
                                {isLoading ? 'Processing...' : 'Merge'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
}

export default MergeSSOAccountsModal;
